import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Grid, IconButton, Input, Paper, Stack } from '@mui/material';
import {
  Cancel as CancelIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CopyAll,
  Add as CreateIcon,
} from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import {
  PostRateMarkupGroupsDTO,
  PutRateMarkupGroupsDTO,
  useDeleteRateMarkupGoupsMutation,
  useGetRateMarkupGroupsQuery,
  usePostRateMarkupGoupsMutation,
  usePutRateMarkupGoupsMutation,
} from '../../api';
import { UTCToDateTime } from '../../utils/dates';
import type { RateMarkup, RateMarkupGroup } from '../../types';
import { useNavigate } from 'react-router-dom';
import { RouterPaths } from '../../router/paths';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import { useAppActions } from '../../utils/hooks';
import { CreateExchangeFeeGroupModal } from './components/CreateExchangeFeeGroupModal';
import { DeleteExchangeFeeGroupModal } from './components/DeleteExchangeFeeGroupModal';
import { permissionsCheck } from '../../utils/permission';

export const ExchangeFeeGroups: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const hasPermissionToViewFee = permissionsCheck(['READ_RATE_MARKUPS']);
  const hasPermissionToEditExchangeFeeGroups = permissionsCheck(['WRITE_RATE_MARKUPS']);

  // api data
  const {
    data: rateMarkups,
    isLoading: isRateMarkupsLoading,
    refetch,
  } = useGetRateMarkupGroupsQuery({});
  const [posttRateMarkupGroup, { isLoading: isRateMarkupPostLoading }] =
    usePostRateMarkupGoupsMutation();
  const [deletetRateMarkupGroup, { isLoading: isRateMarkupDeleteLoading }] =
    useDeleteRateMarkupGoupsMutation();
  const [updateRateMarkup, { isLoading: isUpdateRateMarkupGroupLoading }] =
    usePutRateMarkupGoupsMutation();

  // edit fields logic
  const [editFields, setEditFields] = useState<PostRateMarkupGroupsDTO | null>(null);
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);

  const handleEditFields = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;

    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<number | null>();
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (rateMarkup: RateMarkupGroup) => {
    setEditFields(rateMarkup);
    setActiveRowId(rateMarkup.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId(null);
    setEditMode(false);
  };

  // create modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const [isCopyModalShown, setIsCopyModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
    setIsCopyModalShown(false);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setActiveRowId(null);
    setEditFields(null);
  };
  const openCopyModal = (id: number) => {
    setCreateModalShown(true);
    setIsCopyModalShown(true);
    setActiveRowId(id);
  };

  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (fee: RateMarkupGroup) => {
    setDeleteModalShown(true);
    setActiveRowId(fee.id);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
  };

  useEffect(() => {
    hasPermissionToEditExchangeFeeGroups &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // delete fee handler
  const deleteFeeGroupHandler = async () => {
    if (!activeRowId) return;

    const args = { rate_markup_group_id: activeRowId };

    try {
      await deletetRateMarkupGroup(args).unwrap();
      closeDeleteModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const createFeeGroup = async () => {
    if (!editFields?.name) return;

    const args = {
      rate_markup_group_id: activeRowId ?? undefined,
      name: editFields.name,
    };

    try {
      await posttRateMarkupGroup(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // edit rate markup logic
  const editRateMarkup = async () => {
    if (!editFields || !editFields.name || !activeRowId) return;

    const body: PutRateMarkupGroupsDTO['body'] = {
      name: editFields.name,
    };

    try {
      await updateRateMarkup({ rate_markup_group_id: activeRowId, body }).unwrap();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      offEditMode();
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('exchange-fee-groups.head-cells.id') },
    { id: '2', label: t('exchange-fee-groups.head-cells.name') },
    { id: '3', label: t('exchange-fee-groups.head-cells.create-at') },
    {
      id: '4',
      label: t('exchange-fee-groups.head-cells.actions'),
      hidden: !hasPermissionToEditExchangeFeeGroups,
    },
  ];

  const rows: Row[] | undefined = rateMarkups?.rateMarkupGroups?.map((it) => {
    const { id, name, created_at, is_default } = it;

    const cells: RowCell[] = [
      { name: 'id', value: id },
      {
        name: 'name',
        value: name,
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="name"
              value={editFields?.name ?? ''}
              onChange={(e) => handleEditFields(e)}
              disabled={isUpdateRateMarkupGroupLoading}
            />
          ) : undefined,
      },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditExchangeFeeGroups,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  editRateMarkup();
                }}
                disabled={isUpdateRateMarkupGroupLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  offEditMode();
                }}
                disabled={isUpdateRateMarkupGroupLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="copy-exchange-fee-group"
              color="info"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openCopyModal(id);
              }}
              disabled={isRateMarkupsLoading}
            >
              <CopyAll />
            </IconButton>

            <IconButton
              aria-label="edit"
              color="warning"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                startEditMode(it);
              }}
              disabled={isRateMarkupsLoading}
            >
              <EditIcon />
            </IconButton>

            {!is_default && (
              <IconButton
                aria-label="delete"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDeleteModal(it);
                }}
                disabled={isRateMarkupsLoading}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        ),
      },
    ];

    return {
      cells,
      onClick: () =>
        !isEditMode &&
        hasPermissionToViewFee &&
        navigate(`${RouterPaths.ExchangeFeeGroups}/${id}/exchange-fee`),
      hover: !isEditMode && hasPermissionToViewFee,
      sx: { cursor: 'pointer' },
    };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <DeleteExchangeFeeGroupModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteFeeGroupHandler}
        isLoading={isRateMarkupDeleteLoading}
      />

      <CreateExchangeFeeGroupModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFeeGroup}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isRateMarkupPostLoading}
        isCopy={isCopyModalShown}
      />
    </>
  );
};
